// Real-user Core Web Vitals (RUM) beacon.
//
// Subscribes to the web-vitals library's CLS/INP/LCP/FCP/TTFB callbacks,
// buffers the samples in memory, and flushes a single navigator.sendBeacon
// to /api/analytics/cwv when the page is hidden (Page Lifecycle's
// canonical "fire on the way out" moment).
//
// Design choices:
//   - Buffer + flush, not per-metric beacon. CLS keeps reporting as the
//     page lives; we want the final value, not every interim.
//   - sendBeacon (not fetch) so the request survives the unload. Falls
//     back to a keepalive fetch when sendBeacon isn't available.
//   - Route is the SPA pathname (no query, no hash) so the server
//     dashboard groups by pure route. We capture it at flush time so a
//     single-page navigation reports against the LAST viewed route.
//   - Best-effort: never throws into the React tree. A failed beacon
//     is invisible to the user.
//   - Honor navigator.connection.saveData: if the user opted into
//     reduced data, we don't beacon.

import type { Metric } from 'web-vitals';
import { onCLS, onFCP, onINP, onLCP, onTTFB } from 'web-vitals';
import { setWebVitalTag } from './telemetry';

interface NavigatorConnectionLike {
  saveData?: boolean;
}

const ENDPOINT = '/api/analytics/cwv';
const BUFFER: Array<{
  name: string;
  value: number;
  id: string;
  route: string;
  navigationType?: string;
}> = [];

function currentRoute(): string {
  // Path only — strip query/hash. Truncate at 128 to match server schema.
  const p = window.location.pathname.slice(0, 128);
  return p || '/';
}

function isSaveData(): boolean {
  const c = (navigator as Navigator & { connection?: NavigatorConnectionLike }).connection;
  return c?.saveData === true;
}

function onMetric(m: Metric): void {
  // web-vitals's Metric.navigationType is well-typed to the same enum
  // we accept on the server. Pass through verbatim.
  BUFFER.push({
    name: m.name,
    value: m.value,
    id: m.id,
    route: currentRoute(),
    navigationType: m.navigationType,
  });
  // Mirror the latest reading into telemetry so a subsequent
  // captureException carries the CWV context as Sentry tags. Triage
  // a "regression on slow loads" via tag filter: cwv.lcp > 2500.
  setWebVitalTag(m.name, m.value);
}

function flush(): void {
  if (BUFFER.length === 0 || isSaveData()) {
    BUFFER.length = 0;
    return;
  }
  const payload = JSON.stringify({ samples: BUFFER.splice(0, BUFFER.length) });
  // sendBeacon defaults to "text/plain" so CSRF doesn't apply (per the
  // server endpoint's PUBLIC_PATHS rationale). The server parses
  // request.text() as JSON.
  if ('sendBeacon' in navigator) {
    try {
      navigator.sendBeacon(ENDPOINT, payload);
      return;
    } catch {
      // fall through to keepalive fetch
    }
  }
  try {
    void fetch(ENDPOINT, {
      method: 'POST',
      body: payload,
      keepalive: true,
      // Intentionally no Content-Type — matches sendBeacon's text/plain
      // default so the server doesn't need to handle two shapes.
    });
  } catch {
    // best-effort
  }
}

/**
 * Subscribe to CWV signals + wire the page-hidden flush. Safe to call
 * exactly once at app boot. No-op in non-browser environments (the
 * web-vitals package guards internally).
 */
export function initCwv(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  // SSR-friendly guard — none of these tools should fire on the server.
  onCLS(onMetric);
  onINP(onMetric);
  onLCP(onMetric);
  onFCP(onMetric);
  onTTFB(onMetric);
  // Flush on the way out. visibilitychange + pagehide are the modern
  // pair; the older 'unload' is unreliable on mobile (BFCache).
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
  window.addEventListener('pagehide', flush);
}
